import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FilePlus2, Inbox, Eye, FileEdit, Trash2, Download, Printer } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import TatBadge from "@/components/TatBadge";
import { FORM_TYPE_LABEL } from "@/components/FormDetails";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { formatDate } from "@/lib/dateFmt";
import { STAGE_LABEL } from "@/lib/statusMap";

const TABS = {
  applicant:   [{ id: "mine", label: "My Submissions" }],
  hod:         [{ id: "pending", label: "Pending My Approval" }, { id: "mine", label: "My Submissions" }],
  admin1:      [{ id: "pending", label: "Pending My Approval" }, { id: "mine", label: "My Submissions" }],
  admin1_alt:  [{ id: "pending", label: "Pending My Approval" }, { id: "mine", label: "My Submissions" }],
  admin2:      [{ id: "pending", label: "Pending My Approval" }, { id: "mine", label: "My Submissions" }],
  process_owner: [{ id: "pending", label: "Pending My Approval" }, { id: "mine", label: "My Submissions" }],
  super_admin: [
    { id: "all", label: "All Requisitions" },
    { id: "pending", label: "Pending My Approval" },
    { id: "mine", label: "My Submissions" },
  ],
};

export default function Dashboard() {
  const { user } = useAuth();
  const tabs = TABS[user?.role] || [{ id: "mine", label: "My Submissions" }];
  const [tab, setTab] = useState(tabs[0].id);
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Phase 15 — Dashboard filters (form_type / dept / stage / status).
  const [filters, setFilters] = useState({
    form_type: "", department: "", current_stage: "", status: "",
  });

  const applyFilters = (arr) => arr.filter((r) => {
    if (filters.form_type && r.form_type !== filters.form_type) return false;
    if (filters.department && (r.form_data?.mainDept || "") !== filters.department) return false;
    if (filters.current_stage && r.current_stage !== filters.current_stage) return false;
    if (filters.status && r.status !== filters.status) return false;
    return true;
  });

  const doExport = async () => {
    try {
      const r = await api.get("/dashboard/export.xlsx", {
        params: { ...filters, limit: 5000 }, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dashboard_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const openPrintView = () => {
    const p = new URLSearchParams({ ...filters, tab });
    window.open(`/dashboard/print?${p.toString()}`, "_blank");
  };

  const loadDrafts = () => {
    api.get("/requisitions/drafts").then((r) => setDrafts(r.data || [])).catch(() => {});
  };

  useEffect(() => {
    setLoading(true);
    api.get("/requisitions", { params: { filter: tab } })
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
    loadDrafts();
  }, [tab]);

  const removeDraft = async (id) => {
    try {
      await api.delete(`/requisitions/drafts/${id}`);
      toast.success("Draft discarded");
      loadDrafts();
    } catch { toast.error("Could not delete draft"); }
  };

  const stats = useMemo(() => {
    return {
      total: rows.length,
      pending: rows.filter((r) => !["completed", "rejected"].includes(r.status)).length,
      completed: rows.filter((r) => r.status === "completed").length,
      rejected: rows.filter((r) => ["rejected", "on_hold"].includes(r.status)).length,
    };
  }, [rows]);

  return (
    <Layout>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold">Dashboard</div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1f2320] leading-tight">
            Namaste, {user?.name.split(" ")[0]}.
          </h1>
          <p className="text-sm text-[#4a524c] mt-1">
            Here&apos;s where your requisitions stand today.
          </p>
        </div>
        <Link to="/requisitions/new" className="btn-vgws" data-testid="new-material-cta">
          <FilePlus2 size={16} /> New Requisition
        </Link>
      </div>

      {drafts.length > 0 && (
        <div className="sheet p-5 mb-6 border-l-4 border-l-[#b48a45]" data-testid="drafts-banner">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[#b48a45] font-semibold">Continue draft</div>
              <h2 className="font-serif text-lg font-semibold text-[#1f2320]">
                You have {drafts.length} draft{drafts.length !== 1 ? "s" : ""} in progress
              </h2>
            </div>
          </div>
          <ul className="divide-y divide-[#f2ecdf]">
            {drafts.map((d) => (
              <li key={d.id} className="flex items-center justify-between py-2 gap-2" data-testid={`draft-${d.id}`}>
                <div>
                  <div className="font-medium text-[#1f2320]">
                    {FORM_TYPE_LABEL[d.form_type] || d.form_type}
                  </div>
                  <div className="text-xs text-[#6e7770]">
                    Updated {formatDate(d.updated_at, { includeTime: true })}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Link to={`/requisitions/new/${d.form_type}?draft=${d.id}`}
                        className="link-vgws inline-flex items-center gap-1"
                        data-testid={`resume-draft-${d.id}`}>
                    <FileEdit size={14} /> Resume
                  </Link>
                  <button onClick={() => removeDraft(d.id)}
                          className="text-[#b3261e] hover:underline text-sm inline-flex items-center gap-1"
                          data-testid={`delete-draft-${d.id}`}>
                    <Trash2 size={14} /> Discard
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Total" value={stats.total} tone="ink" testid="stat-total" />
        <Stat label="In progress" value={stats.pending} tone="teal" testid="stat-pending" />
        <Stat label="Completed" value={stats.completed} tone="emerald" testid="stat-completed" />
        <Stat label="Rejected / On hold" value={stats.rejected} tone="danger" testid="stat-rejected" />
      </div>

      <div className="sheet-plain overflow-hidden">
        {/* Phase 15 — Filters + export/print row */}
        <div className="flex flex-wrap items-end gap-2 p-3 border-b border-[#e2d7c3] bg-[#faf5eb]">
          <select className="input-vgws text-xs !py-1 min-w-[140px]"
                  value={filters.form_type}
                  onChange={(e) => setFilters({ ...filters, form_type: e.target.value })}
                  data-testid="filter-form-type">
            <option value="">All form types</option>
            {Object.entries(FORM_TYPE_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <input className="input-vgws text-xs !py-1 min-w-[140px]"
                  placeholder="Department…"
                  value={filters.department}
                  onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                  data-testid="filter-department" />
          <input className="input-vgws text-xs !py-1 min-w-[140px]"
                  placeholder="Current stage…"
                  value={filters.current_stage}
                  onChange={(e) => setFilters({ ...filters, current_stage: e.target.value })}
                  data-testid="filter-current-stage" />
          <select className="input-vgws text-xs !py-1 min-w-[140px]"
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  data-testid="filter-status">
            <option value="">All status</option>
            {["submitted","hod_review","admin1_review","admin2_review",
              "process_owner_review","accounts_review","completed","rejected","on_hold"].map((s) => (
              <option key={s} value={s}>{STAGE_LABEL[s] || s}</option>
            ))}
          </select>
          <div className="flex-grow" />
          <button className="btn-vgws btn-teal text-xs !py-1"
                  onClick={doExport} data-testid="btn-download-sheet">
            <Download size={12} /> Download Sheet
          </button>
          <button className="btn-ghost text-xs !py-1"
                  onClick={openPrintView} data-testid="btn-print-view">
            <Printer size={12} /> Print View
          </button>
        </div>
        <div className="flex flex-wrap border-b border-[#e2d7c3] bg-[#faf5eb]">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 sm:px-5 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                tab === t.id
                  ? "border-[#7a1f2b] text-[#7a1f2b] bg-white"
                  : "border-transparent text-[#4a524c] hover:text-[#7a1f2b]"
              }`}
              data-testid={`dashboard-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="p-10 text-center text-[#6e7770]" data-testid="dashboard-loading">Loading…</div>
        ) : applyFilters(rows).length === 0 ? (
          <EmptyState tab={tab} />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="requisitions-table">
              <thead>
                <tr className="bg-white text-left">
                  <th className="px-4 py-3 font-serif font-semibold text-[#7a1f2b]">Ref</th>
                  <th className="px-4 py-3 font-serif font-semibold text-[#7a1f2b]">Type</th>
                  <th className="px-4 py-3 font-serif font-semibold text-[#7a1f2b]">Applicant</th>
                  <th className="px-4 py-3 font-serif font-semibold text-[#7a1f2b]">Department</th>
                  <th className="px-4 py-3 font-serif font-semibold text-[#7a1f2b]">Current stage</th>
                  <th className="px-4 py-3 font-serif font-semibold text-[#7a1f2b]">Status</th>
                  <th className="px-4 py-3 font-serif font-semibold text-[#7a1f2b]">Submitted</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {applyFilters(rows).map((r) => (
                  <tr key={r.id} className="border-t border-[#f2ecdf] hover:bg-[#faf5eb] transition-colors" data-testid={`requisition-row-${r.ref_code}`}>
                    <td className="px-4 py-3 font-mono text-[13px] text-[#7a1f2b] font-semibold whitespace-nowrap">{r.ref_code}</td>
                    <td className="px-4 py-3 text-xs">
                      <span className="inline-block px-2 py-1 rounded-full bg-[#f4e9eb] text-[#7a1f2b] font-medium" data-testid={`req-type-badge-${r.ref_code}`}>
                        {FORM_TYPE_LABEL[r.form_type] || r.form_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#1f2320]">{r.applicant_name}</div>
                      <div className="text-xs text-[#6e7770]">{r.applicant_email}</div>
                    </td>
                    <td className="px-4 py-3 text-[#4a524c]">
                      {r.form_data?.mainDept || "—"}
                      <div className="text-xs text-[#6e7770]">{r.form_data?.subDept}</div>
                    </td>
                    <td className="px-4 py-3 text-[#4a524c]">{STAGE_LABEL[r.current_stage] || r.current_stage}</td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} formType={r.form_type} />
                      {r.is_emergency && (
                        <span className="ml-2 badge bg-[#b3261e] text-white border-[#b3261e]" data-testid={`emergency-flag-${r.ref_code}`}>🚨 EMERGENCY</span>
                      )}
                      {r.late_submission && (
                        <span className="ml-2 badge bg-[#fcebe9] text-[#b3261e] border-[#b3261e]/40" data-testid={`late-flag-${r.ref_code}`}>LATE</span>
                      )}
                      {r.form_type === "material" && (r.form_data?.items || []).length > 0 && (() => {
                        const items = r.form_data.items;
                        const s = items.reduce((acc, it) => {
                          const c = (it.admin_status || {}).current;
                          if (c === "Approved") acc.a += 1;
                          else if (c === "On Hold") acc.h += 1;
                          else if (c === "Rejected") acc.r += 1;
                          return acc;
                        }, { a: 0, h: 0, r: 0 });
                        if (!(s.a || s.h || s.r)) return null;
                        return (
                          <span className="ml-2 badge bg-[#faf5eb] text-[#4a524c] border-[#e2d7c3]"
                                title={`Admin-1 item decisions (${items.length} items)`}
                                data-testid={`item-status-summary-${r.ref_code}`}>
                            {s.a}✓ · {s.h}⏸ · {s.r}✗
                          </span>
                        );
                      })()}
                      <div className="mt-1"><TatBadge deadline={r.tat_deadline} current_stage={r.current_stage} status={r.status} /></div>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6e7770] whitespace-nowrap">
                      {r.submitted_at ? formatDate(r.submitted_at) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/requisitions/${r.ref_code}`}
                        className="inline-flex items-center gap-1 text-[#7a1f2b] font-medium text-sm hover:underline"
                        data-testid={`view-requisition-${r.ref_code}`}
                      >
                        <Eye size={14} /> View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}

function Stat({ label, value, tone, testid }) {
  const map = {
    ink: "text-[#1f2320]",
    teal: "text-[#2d6b6b]",
    emerald: "text-emerald-700",
    danger: "text-[#b3261e]",
  };
  return (
    <div className="sheet-plain p-4 hover-lift" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-[#6e7770] font-medium">{label}</div>
      <div className={`font-serif text-3xl font-bold ${map[tone]} mt-1`}>{value}</div>
    </div>
  );
}

function EmptyState({ tab }) {
  const messages = {
    mine: "You haven't submitted a requisition yet.",
    pending: "Nothing awaiting your approval. Enjoy the calm.",
    all: "The portal has no requisitions yet.",
  };
  return (
    <div className="p-10 text-center" data-testid="dashboard-empty">
      <Inbox size={40} className="mx-auto text-[#cbbba0]" />
      <div className="mt-3 text-[#4a524c]">{messages[tab] || "No records."}</div>
      {tab === "mine" && (
        <Link to="/requisitions/new" className="btn-vgws mt-4 inline-flex">
          <FilePlus2 size={16} /> Create your first requisition
        </Link>
      )}
    </div>
  );
}
